import { NavLink } from 'react-router-dom';

import { isNavItemVisible, navigation } from '@/app/navigation';
import { useAuth } from '@/shared/auth/useAuth';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/Icon';
import { LogoLockup } from '@/shared/ui/Logo';

export function Sidebar({
  collapsed,
  onToggle,
  onNavigate,
}: {
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}) {
  const { session, can, hasFeature } = useAuth();

  // Um item so aparece quando a empresa contratou a funcionalidade e o usuario
  // tem a permissao, e quando o item e do publico da sessao (seguradora ou
  // corretora). Um grupo sem itens visiveis desaparece junto, para nao deixar um
  // titulo orfao no menu.
  const acesso = { can, hasFeature, brokerScoped: session?.broker_scoped === true };
  const groups = navigation
    .map((group) => ({
      label: group.label,
      items: group.items.filter((item) => isNavItemVisible(item, acesso)),
    }))
    .filter((group) => group.items.length > 0);

  const branding = session?.tenant.branding;

  return (
    <aside
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-white/5',
        'bg-linear-to-b from-brand-950 via-brand-900 to-brand-950',
        'transition-[width] duration-(--duration-base) ease-(--ease-out-soft)',
        collapsed ? 'w-[76px]' : 'w-[264px]',
      )}
    >
      <div className="flex h-16 items-center border-b border-white/5 px-4">
        {collapsed ? (
          <LogoLockup
            name=""
            {...(branding?.logo_symbol_url
              ? { symbolSrc: branding.logo_symbol_url }
              : {})}
          />
        ) : (
          <LogoLockup
            name={branding?.display_name || 'SeguraCore'}
            tagline="Seguro garantia e fianca"
            {...(branding?.logo_symbol_url
              ? { symbolSrc: branding.logo_symbol_url }
              : {})}
          />
        )}
      </div>

      <nav
        aria-label="Navegacao principal"
        className="flex-1 overflow-y-auto px-3 py-3"
      >
        {groups.map((group) => (
          <div key={group.label} className="mb-1">
            {!collapsed ? (
              <p className="px-2 pt-4 pb-1.5 text-[0.625rem] font-semibold tracking-wider text-white/50 uppercase">
                {group.label}
              </p>
            ) : (
              <div className="my-3 border-t border-white/5" aria-hidden="true" />
            )}

            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.key}>
                  <NavLink
                    to={item.path}
                    end={item.path === '/'}
                    onClick={onNavigate}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-lg px-2.5 py-2 text-[0.8125rem]',
                        'transition-[background-color,color,box-shadow] duration-(--duration-fast)',
                        'focus-visible:outline-2 focus-visible:outline-offset-2',
                        'focus-visible:outline-accent-400',
                        collapsed && 'justify-center px-0',
                        // O item aberto tem a cor de acao da empresa, com o brilho do
                        // degrade da marca; os outros ficam no claro sobre o escuro.
                        isActive
                          ? 'bg-brand-500 bg-linear-to-r from-brand-500 to-brand-600 font-medium text-white shadow-(--shadow-brand)'
                          : 'text-white/70 hover:bg-white/8 hover:text-white',
                      )
                    }
                  >
                    <Icon name={item.icon} size={17} className="shrink-0" />
                    {!collapsed ? <span className="truncate">{item.label}</span> : null}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-white/5 p-3">
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-md border border-white/10',
            'px-2 py-2 text-xs text-white/50 transition-colors duration-(--duration-fast)',
            'hover:border-white/20 hover:text-white',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-400',
          )}
        >
          <Icon name={collapsed ? 'chevronRight' : 'chevronLeft'} size={15} />
          {!collapsed ? <span>Recolher menu</span> : null}
        </button>
      </div>
    </aside>
  );
}
